// Production Stage 2 checkout API client.
//
// HARD BOUNDARIES (enforced by tests + code review):
// - Only used when VITE_ENABLE_CHECKOUT_STAGE2_API === "true".
// - Targets the production API base (VITE_CHECKOUT_API_BASE_URL, default
//   https://api2.paworld365.com).
// - Implements ONLY the Stage 2 surface that backend has flagged on:
//     POST /api/checkout/sessions                  (create session + buyer)
//     POST /api/checkout/{cs}/quote                (persisted quote)
//     GET  /api/checkout/{cs}/status               (status read)
//     POST /api/checkout/quote                     (optional dry-run)
// - NEVER calls /confirm, /payment, Stripe, finance-admin, Dataverse, internal admin.
// - NEVER computes tax — money/tax values come verbatim from API responses.
// - NEVER sends custom request headers. Only Accept + Content-Type. The
//   session clientRequestId travels in the JSON body. No X-Client-Request-Id,
//   no X-Request-Id, no X-Idempotency-Key, no Authorization. This avoids
//   depending on a CORS_ALLOWED_HEADERS extension.
// - Rejects GUID-shaped IDs before sending. Scrubs GUID-shaped strings from
//   unexpected response payloads before returning them.
//
// WIRE CONTRACTS (canonical — DO NOT DEVIATE):
//   Quote (POST /api/checkout/quote and POST /api/checkout/{cs}/quote):
//     {
//       items: [{ cartLineKey, offeringPublicId, priceBookEntryPublicId, quantity }],
//       buyerContext: {
//         buyerCountryIso, billingCountryIso, customerType,
//         vatNumber?, vatValidationStatus, vatValidationSource,
//         buyerRegion?, buyerCity?, accountPublicId?, contactPublicId?
//       }
//     }
//     No top-level `mode`, no `checkoutSessionPublicId` in body.
//
//   Session (POST /api/checkout/sessions):
//     {
//       clientRequestId,
//       buyer: {
//         email, firstName, lastName, companyName?, countryIso?, phone?,
//         billingStreet?, billingCity?, billingState?, billingPostcode?
//       }
//     }
//     No offeringPublicId/eventPublicId/items/money in this body.
//
// Internal frontend buyer/form state uses friendlier names. Always translate
// at the wire boundary via the serializers below.

import type { CheckoutQuoteItemInput, CheckoutQuoteResponse } from "./quoteTypes";
import { scrubGuids } from "./devApi";
import { assertCheckoutMutationAllowed } from "./productionMode";

const DEFAULT_PROD_BASE = "https://api2.paworld365.com";

export const CHECKOUT_STAGE2_API_ENABLED =
  String(import.meta.env.VITE_ENABLE_CHECKOUT_STAGE2_API ?? "false").toLowerCase() === "true";

/**
 * Hard gate for any future Pay / Confirm CTA in the Stage 2 flow.
 * Default OFF. Additional runtime guards apply (see CheckoutFormStage2):
 * buyer must be linked, persisted quote must match displayed gross, quote must
 * be priced AND tax calculated, etc. status.nextAction alone MUST NEVER
 * enable a real payment CTA.
 */
export const CHECKOUT_PAYMENT_ENABLED =
  String(import.meta.env.VITE_ENABLE_CHECKOUT_PAYMENT ?? "false").toLowerCase() === "true";

export const CHECKOUT_STAGE2_API_BASE_URL = String(
  import.meta.env.VITE_CHECKOUT_API_BASE_URL ?? DEFAULT_PROD_BASE,
).replace(/\/+$/, "");

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
// Strict checkout-session public-ID rule. Matches exactly:
//   cs_  followed by one or more lowercase alphanumerics, underscores or hyphens
// and nothing else. Rejects: GUIDs, csx123, cs_123 followed by slash/query/junk,
// trailing whitespace, mixed-case variants and any other shape.
const PUBLIC_CS_RE = /^cs_[a-z0-9_-]+$/;
const OFFERING_PREFIX_RE = /^of_[A-Za-z0-9_-]+$/;
const PBE_PREFIX_RE = /^pbe_[A-Za-z0-9_-]+$/;
const QUANTITY_MAX = 999;

function assertPublicId(id: string, label: string): void {
  if (!id) throw new Error(`${label} required`);
  if (GUID_RE.test(id)) {
    throw new Error(`${label}: GUID-shaped id rejected`);
  }
}

export function isValidCheckoutSessionPublicId(id: string | null | undefined): boolean {
  if (!id || typeof id !== "string") return false;
  if (GUID_RE.test(id)) return false;
  return PUBLIC_CS_RE.test(id);
}

function assertCheckoutSessionPublicId(id: string): void {
  assertPublicId(id, "checkoutSessionPublicId");
  if (!PUBLIC_CS_RE.test(id)) {
    throw new Error("checkoutSessionPublicId: expected cs_-prefixed public id (^cs_[a-z0-9-]+$)");
  }
}

function assertQuantity(q: string, label: string): void {
  const trimmed = String(q ?? "").trim();
  if (!/^[1-9][0-9]*$/.test(trimmed)) {
    throw new Error(`${label}: expected positive integer string`);
  }
  if (Number(trimmed) > QUANTITY_MAX) {
    throw new Error(`${label}: exceeds upper bound ${QUANTITY_MAX}`);
  }
}

function assertItemsPublic(items: CheckoutQuoteItemInput[]): void {
  for (const i of items) {
    assertPublicId(i.offeringPublicId, "offeringPublicId");
    assertPublicId(i.priceBookEntryPublicId, "priceBookEntryPublicId");
    if (!OFFERING_PREFIX_RE.test(i.offeringPublicId)) {
      throw new Error("offeringPublicId: expected of_-prefixed public id");
    }
    if (!PBE_PREFIX_RE.test(i.priceBookEntryPublicId)) {
      throw new Error("priceBookEntryPublicId: expected pbe_-prefixed public id");
    }
    assertQuantity(i.quantity, "item.quantity");
  }
}

function assertBuyerContextSafe(ctx: QuoteBuyerContextInput): void {
  if (ctx.accountPublicId && GUID_RE.test(ctx.accountPublicId)) {
    throw new Error("accountPublicId: GUID-shaped id rejected");
  }
  if (ctx.contactPublicId && GUID_RE.test(ctx.contactPublicId)) {
    throw new Error("contactPublicId: GUID-shaped id rejected");
  }
}

export class CheckoutStage2ApiError extends Error {
  status: number;
  body: unknown;
  correlationId: string | null;
  constructor(status: number, body: unknown, message: string, correlationId: string | null = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.correlationId = correlationId;
  }
}

function pickCorrelationId(res: Response): string | null {
  return res.headers.get("x-correlation-id") || res.headers.get("x-request-id") || null;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

interface ClientInit {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
}

function resolveBase(init?: ClientInit): string {
  return (init?.baseUrl ?? CHECKOUT_STAGE2_API_BASE_URL).replace(/\/+$/, "");
}

/**
 * Build the explicit request headers for every Stage 2 call.
 * MUST contain only Accept and (for bodied requests) Content-Type.
 * No custom headers. No idempotency header. The browser-supplied Origin is
 * not under this allowlist.
 */
function buildHeaders(hasBody = false): HeadersInit {
  const h: Record<string, string> = { Accept: "application/json" };
  if (hasBody) h["Content-Type"] = "application/json";
  return h;
}

// ---------------------------------------------------------------------------
// Canonical wire input types (FE-facing). These are what the rest of the app
// passes in. The serializers below translate to exact wire field names.
// ---------------------------------------------------------------------------

export type QuoteCustomerType = "consumer" | "business" | "unknown";

export type QuoteVatValidationStatus =
  | "not_provided"
  | "not_checked"
  | "valid"
  | "invalid"
  | "manual_confirmed";

export type QuoteVatValidationSource =
  | "none"
  | "provided_by_customer"
  | "manual"
  | "external_service"
  | "legacy";

export interface QuoteBuyerContextInput {
  buyerCountryIso: string;
  billingCountryIso?: string;
  customerType: QuoteCustomerType;
  vatNumber?: string;
  vatValidationStatus: QuoteVatValidationStatus;
  vatValidationSource: QuoteVatValidationSource;
  buyerRegion?: string;
  buyerCity?: string;
  accountPublicId?: string;
  contactPublicId?: string;
}

export interface SessionBuyerIdentityInput {
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  /** ISO-3166 alpha-2. Maps to wire `buyer.countryIso`. */
  countryIso?: string;
  phone?: string;
  /** Accepted bill-to snapshot; persisted on the Checkout Session before quote. */
  billingStreet?: string;
  billingCity?: string;
  billingState?: string;
  billingPostcode?: string;
}

// ---------------------------------------------------------------------------
// Wire serializers (pure functions — exported for conformance tests).
// ---------------------------------------------------------------------------

export function serializeQuoteBuyerContext(
  ctx: QuoteBuyerContextInput,
): Record<string, unknown> {
  const buyer = (ctx.buyerCountryIso || "").toUpperCase();
  const billing = (ctx.billingCountryIso ?? ctx.buyerCountryIso ?? "").toUpperCase();
  const out: Record<string, unknown> = {
    buyerCountryIso: buyer,
    billingCountryIso: billing,
    customerType: ctx.customerType,
    vatValidationStatus: ctx.vatValidationStatus,
    vatValidationSource: ctx.vatValidationSource,
  };
  if (ctx.vatNumber && ctx.vatNumber.trim()) out.vatNumber = ctx.vatNumber.trim();
  if (ctx.buyerRegion && ctx.buyerRegion.trim()) out.buyerRegion = ctx.buyerRegion.trim();
  if (ctx.buyerCity && ctx.buyerCity.trim()) out.buyerCity = ctx.buyerCity.trim();
  if (ctx.accountPublicId) out.accountPublicId = ctx.accountPublicId;
  if (ctx.contactPublicId) out.contactPublicId = ctx.contactPublicId;
  return out;
}

export function serializeQuoteItems(items: CheckoutQuoteItemInput[]): Record<string, unknown>[] {
  return (items ?? []).map((i) => ({
    cartLineKey: i.cartLineKey,
    offeringPublicId: i.offeringPublicId,
    priceBookEntryPublicId: i.priceBookEntryPublicId,
    quantity: i.quantity,
  }));
}

export function serializeSessionBuyerIdentity(
  b: SessionBuyerIdentityInput,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    email: b.email,
    firstName: b.firstName,
    lastName: b.lastName,
  };
  if (b.companyName && b.companyName.trim()) out.companyName = b.companyName.trim();
  if (b.countryIso && b.countryIso.trim()) out.countryIso = b.countryIso.toUpperCase();
  if (b.phone && b.phone.trim()) out.phone = b.phone.trim();
  if (b.billingStreet && b.billingStreet.trim()) out.billingStreet = b.billingStreet.trim();
  if (b.billingCity && b.billingCity.trim()) out.billingCity = b.billingCity.trim();
  if (b.billingState && b.billingState.trim()) out.billingState = b.billingState.trim();
  if (b.billingPostcode && b.billingPostcode.trim()) out.billingPostcode = b.billingPostcode.trim();
  return out;
}

// ---------------------------------------------------------------------------
// Session creation
// ---------------------------------------------------------------------------

export interface CreateCheckoutSessionRequest {
  buyer: SessionBuyerIdentityInput;
  /** Idempotency key — stable per buyer attempt. Must begin `crq_`, not a GUID. */
  clientRequestId: string;
}

export interface CreateCheckoutSessionResponse {
  checkoutSessionPublicId: string;
  state?: string;
  created?: boolean;
  [key: string]: unknown;
}

export async function createCheckoutSession(
  req: CreateCheckoutSessionRequest,
  init?: ClientInit,
): Promise<CreateCheckoutSessionResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/sessions");
  const base = resolveBase(init);
  const fetchImpl = init?.fetchImpl ?? fetch;
  if (!req.buyer?.email) throw new Error("buyer.email required");
  if (!req.clientRequestId) throw new Error("clientRequestId required");
  if (GUID_RE.test(req.clientRequestId)) {
    throw new Error("clientRequestId: GUID-shaped id rejected");
  }

  const body = {
    clientRequestId: req.clientRequestId,
    buyer: serializeSessionBuyerIdentity(req.buyer),
  };

  const res = await fetchImpl(`${base}/api/checkout/sessions`, {
    method: "POST",
    headers: buildHeaders(true),
    body: JSON.stringify(body),
  });
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `createCheckoutSession failed: ${res.status}`,
      pickCorrelationId(res),
    );
  }
  return scrubGuids(parsed) as CreateCheckoutSessionResponse;
}

// ---------------------------------------------------------------------------
// Persisted quote (session-scoped)
// ---------------------------------------------------------------------------

export interface PersistQuoteRequest {
  buyerContext: QuoteBuyerContextInput;
  items: CheckoutQuoteItemInput[];
  /**
   * Optional promo code entered by the buyer. Forwarded verbatim (trimmed)
   * to the backend quote endpoint. Empty / whitespace-only values are omitted
   * from the wire body. The FE never derives a discount from this value.
   */
  promoCode?: string;
}

function buildQuoteBody(req: PersistQuoteRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {
    items: serializeQuoteItems(req.items),
    buyerContext: serializeQuoteBuyerContext(req.buyerContext),
  };
  const code = (req.promoCode ?? "").trim();
  if (code) body.promoCode = code;
  return body;
}

export async function persistCheckoutQuote(
  checkoutSessionPublicId: string,
  req: PersistQuoteRequest,
  init?: ClientInit,
): Promise<CheckoutQuoteResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/{cs}/quote");
  assertCheckoutSessionPublicId(checkoutSessionPublicId);
  assertItemsPublic(req.items ?? []);
  assertBuyerContextSafe(req.buyerContext);

  const base = resolveBase(init);
  const fetchImpl = init?.fetchImpl ?? fetch;

  const body = buildQuoteBody(req);

  const res = await fetchImpl(
    `${base}/api/checkout/${encodeURIComponent(checkoutSessionPublicId)}/quote`,
    {
      method: "POST",
      headers: buildHeaders(true),
      body: JSON.stringify(body),
    },
  );
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `persistCheckoutQuote failed: ${res.status}`,
      pickCorrelationId(res),
    );
  }
  return scrubGuids(parsed) as CheckoutQuoteResponse;
}

// ---------------------------------------------------------------------------
// Optional dry-run quote (session not required)
// ---------------------------------------------------------------------------

export async function dryRunQuote(
  req: PersistQuoteRequest,
  init?: ClientInit,
): Promise<CheckoutQuoteResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/quote");
  assertItemsPublic(req.items ?? []);
  assertBuyerContextSafe(req.buyerContext);
  const base = resolveBase(init);
  const fetchImpl = init?.fetchImpl ?? fetch;

  const body = buildQuoteBody(req);

  const res = await fetchImpl(`${base}/api/checkout/quote`, {
    method: "POST",
    headers: buildHeaders(true),
    body: JSON.stringify(body),
  });
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `dryRunQuote failed: ${res.status}`,
      pickCorrelationId(res),
    );
  }
  return scrubGuids(parsed) as CheckoutQuoteResponse;
}

// ---------------------------------------------------------------------------
// Status — complete typed contract (consumed by Step 2 polling later).
// ---------------------------------------------------------------------------

/** Known checkout session states. Unknown future values handled safely (never success). */
export type KnownCheckoutState =
  | "disabled"
  | "not_found"
  | "draft"
  | "quote_required"
  | "quote_blocked"
  | "quoted"
  | "confirmed_invoice_created"
  | "payment_not_started"
  | "payment_session_created"
  | "payment_pending"
  | "payment_settled_release_pending"
  | "release_partially_complete"
  | "access_released"
  | "release_blocked_manual_review"
  | "cancelled"
  | "failed"
  | "quote_locked_payment_pending"
  | "payment_received_order_pending"
  | "payment_received_order_failed"
  | "invoice_created_allocation_pending"
  | "payment_received_review_required"
  | "payment_settled_release_pending_review"
  /** Explicit zero-cost states. Complimentary is not a paid state. */
  | "complimentary_complete"
  | "complimentary_pending_release"
  | "complimentary_blocked"
  /** Backward-compatible spellings accepted during the additive rollout. */
  | "complimentary_access_released"
  | "complimentary_release_pending"
  | "complimentary_release_blocked";

export type CheckoutState = KnownCheckoutState | (string & {});

/** Known next-action hints. Unknown values must never enable Continue/Confirm/Payment. */
export type KnownCheckoutNextAction =
  | "start_quote"
  | "fix_quote"
  | "confirm"
  | "start_payment"
  | "wait_for_payment"
  | "wait_for_release"
  | "view_access"
  | "contact_support"
  | "none";

export type CheckoutNextAction = KnownCheckoutNextAction | (string & {});

/** Known public payment statuses. Unknown values must never be interpreted as settled. */
export type KnownCheckoutPaymentStatus =
  | "not_required"
  | "not_started"
  | "session_created"
  | "pending"
  | "settled"
  | "failed"
  | "unavailable";

export type CheckoutPaymentStatus = KnownCheckoutPaymentStatus | (string & {});

/** Public invoice status is additive because older status payloads nest it. */
export type CheckoutInvoiceStatus = "not_required" | (string & {});

export interface CheckoutStatusTotals {
  net?: string;
  tax?: string;
  gross?: string;
  paid?: string;
  outstanding?: string;
}

export interface CheckoutStatusQuoteBlock {
  status?: string;
  hash?: string;
  pricedAt?: string;
  expiresAt?: string;
}

export interface CheckoutStatusOrderBlock {
  orderKey?: string;
  status?: string;
}

export interface CheckoutStatusInvoiceBlock {
  status?: string;
  paid?: boolean;
  sent?: boolean;
  posted?: boolean;
  publicReference?: string;
}

export interface CheckoutStatusPaymentBlock {
  status: CheckoutPaymentStatus;
  provider?: string;
}

export interface CheckoutStatusReleaseComponent {
  componentType: string;
  label?: string;
  status: string;
  reason?: string;
}

export interface CheckoutStatusReleaseBlock {
  status?: string;
  /** API #854 contract uses `state`; retain `status` for existing consumers. */
  state?: string;
  reason?: string;
  components?: CheckoutStatusReleaseComponent[];
}

/** Server-owned sold evidence for the complimentary presentation. */
export interface CheckoutStatusMoneyBreakdown {
  currency?: string;
  originalGross?: string;
  discountTotal?: string;
  finalGross?: string;
  discountLabel?: string;
}

export interface CheckoutStatusBuyerBlock {
  buyerLinked: boolean;
}

/**
 * Public-safe acceptance state for this Checkout Session. The API deliberately
 * exposes no acceptance/context hashes; exact session/quote/Order binding is
 * verified again by the payment endpoint before any Stripe mutation.
 */
export type CheckoutAcceptanceStatus =
  | "missing"
  | "accepted"
  | "stale"
  | (string & {});

export interface CheckoutStatusAcceptanceBlock {
  status: CheckoutAcceptanceStatus;
  acceptancePublicId?: string;
  contractLocale?: string;
}

export interface CheckoutStatusWarning {
  code: string;
  message: string;
  field?: string;
}

export interface CheckoutStatusResponse {
  checkoutSessionPublicId: string;
  state: CheckoutState;
  nextAction: CheckoutNextAction;
  warnings: CheckoutStatusWarning[];
  currency?: string;
  totals?: CheckoutStatusTotals;
  quote?: CheckoutStatusQuoteBlock;
  order?: CheckoutStatusOrderBlock;
  invoice?: CheckoutStatusInvoiceBlock;
  payment?: CheckoutStatusPaymentBlock;
  release?: CheckoutStatusReleaseBlock;
  /** Explicit API commercial truth; never inferred from client-side totals. */
  commercialState?: "payable" | "complimentary" | (string & {});
  paymentRequired?: boolean;
  invoiceRequired?: boolean;
  /** Flat mirrors accepted from the public zero-cost contract. */
  paymentStatus?: CheckoutPaymentStatus;
  invoiceStatus?: CheckoutInvoiceStatus;
  moneyBreakdown?: CheckoutStatusMoneyBreakdown;
  buyer?: CheckoutStatusBuyerBlock;
  acceptance?: CheckoutStatusAcceptanceBlock;
  correlationId?: string;
  [key: string]: unknown;
}

/** Defensive helpers for unknown future values. */
const KNOWN_STATES = new Set<string>([
  "disabled","not_found","draft","quote_required","quote_blocked","quoted",
  "confirmed_invoice_created","payment_not_started","payment_session_created",
  "payment_pending","payment_settled_release_pending","release_partially_complete",
  "access_released","release_blocked_manual_review","cancelled","failed",
  "quote_locked_payment_pending","payment_received_order_pending",
  "payment_received_order_failed","invoice_created_allocation_pending",
  "payment_received_review_required","payment_settled_release_pending_review",
  "complimentary_complete","complimentary_pending_release","complimentary_blocked",
  "complimentary_access_released","complimentary_release_pending","complimentary_release_blocked",
]);
const KNOWN_NEXT_ACTIONS = new Set<string>([
  "start_quote","fix_quote","confirm","start_payment","wait_for_payment",
  "wait_for_release","view_access","contact_support","none",
]);
const KNOWN_PAYMENT_STATUSES = new Set<string>([
  "not_required","not_started","session_created","pending","settled","failed","unavailable",
]);

export function isKnownCheckoutState(s: string | null | undefined): s is KnownCheckoutState {
  return typeof s === "string" && KNOWN_STATES.has(s);
}
export function isKnownCheckoutNextAction(s: string | null | undefined): s is KnownCheckoutNextAction {
  return typeof s === "string" && KNOWN_NEXT_ACTIONS.has(s);
}
export function isKnownCheckoutPaymentStatus(s: string | null | undefined): s is KnownCheckoutPaymentStatus {
  return typeof s === "string" && KNOWN_PAYMENT_STATUSES.has(s);
}

/** Never interpret unknown payment status as settled. */
export function isPaymentSettled(s: string | null | undefined): boolean {
  return s === "settled";
}

function isStatusShapedSafeNon2xx(
  body: unknown,
  cs: string,
  httpStatus: number,
): boolean {
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  const b = body as Record<string, unknown>;
  if (b.checkoutSessionPublicId !== cs) return false;
  if (b.nextAction !== "none") return false;
  if (!Array.isArray(b.warnings)) return false;
  if (httpStatus === 404 && b.state !== "not_found") return false;
  if (httpStatus === 503 && b.state !== "disabled") return false;
  return true;
}

export async function getCheckoutStatus(
  checkoutSessionPublicId: string,
  init?: ClientInit & { signal?: AbortSignal },
): Promise<CheckoutStatusResponse> {
  assertCheckoutSessionPublicId(checkoutSessionPublicId);
  const base = resolveBase(init);
  const fetchImpl = init?.fetchImpl ?? fetch;
  const res = await fetchImpl(
    `${base}/api/checkout/${encodeURIComponent(checkoutSessionPublicId)}/status`,
    { method: "GET", headers: buildHeaders(false), signal: init?.signal },
  );
  const parsed = await readJson(res);
  if (!res.ok) {
    // Accept ONLY status-shaped 404 (not_found) and 503 (disabled) responses
    // with a matching cs_, expected state, and well-formed nextAction/warnings.
    if (
      (res.status === 404 || res.status === 503) &&
      isStatusShapedSafeNon2xx(parsed, checkoutSessionPublicId, res.status)
    ) {
      return scrubGuids(parsed) as CheckoutStatusResponse;
    }
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `getCheckoutStatus failed: ${res.status}`,
      pickCorrelationId(res),
    );
  }
  const scrubbed = scrubGuids(parsed);
  if (
    !scrubbed ||
    typeof scrubbed !== "object" ||
    (scrubbed as any).checkoutSessionPublicId !== checkoutSessionPublicId
  ) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      "getCheckoutStatus: response session id mismatch",
      pickCorrelationId(res),
    );
  }
  return scrubbed as CheckoutStatusResponse;
}
